import { VERTICALS, getVertical } from '@/shared/constants/verticals';
import apiClient from '@/shared/api/client';
import { ApiResponse } from '@/shared/api/types';

// Central practice-staff roster (providers, hygienists, assistants,
// sterilization techs, clinical team managers, front desk, office managers) — distinct from Team members (dashboard login
// accounts with roles owner/admin/manager/viewer, see teamApi.ts).
// StaffMember rows are always scoped to a single location.
export type StaffRole =
  | 'provider'
  | 'hygienist'
  | 'assistant'
  | 'sterilization_tech'
  | 'clinical_team_manager'
  | 'front_desk'
  | 'office_manager';

/**
 * @deprecated Dental-only. Use `staffRoleOptions(vertical)` — which roles exist
 * and what they are called differs by industry (medicine has no hygienist).
 */
export const STAFF_ROLE_OPTIONS: { value: StaffRole; label: string }[] = VERTICALS.dental
  .staffRoles as { value: StaffRole; label: string }[];

/** Staff roles offered by a vertical, with its own labels. */
export function staffRoleOptions(vertical: string | null | undefined): {
  value: StaffRole;
  label: string;
}[] {
  return getVertical(vertical).staffRoles as { value: StaffRole; label: string }[];
}

const ADMIN_ONLY_ROLES: StaffRole[] = ['front_desk', 'office_manager'];

export function isAdminOnlyStaffRoles(roles: StaffRole[]): boolean {
  return roles.length > 0 && roles.every((r) => ADMIN_ONLY_ROLES.includes(r));
}

export function staffRoleLabel(role: StaffRole, vertical?: string | null): string {
  return staffRoleOptions(vertical).find((r) => r.value === role)?.label ?? role;
}

export interface StaffMember {
  id: string;
  locationId: string;
  name: string;
  pronouns: string | null;
  roles: StaffRole[];
  bbpExposed: boolean;
  active: boolean;
  source: string;
  createdAt: string;
  updatedAt: string;
}

export type StaffPronouns = 'she/her' | 'he/him' | 'they/them';

export interface StaffInput {
  name: string;
  pronouns?: StaffPronouns | null;
  roles: StaffRole[];
  bbpExposed?: boolean;
}

export interface StaffPatch {
  name?: string;
  pronouns?: StaffPronouns | null;
  roles?: StaffRole[];
  bbpExposed?: boolean;
  active?: boolean;
}

export const staffApi = {
  listStaff: async (
    locationId: string,
    includeInactive?: boolean,
  ): Promise<ApiResponse<StaffMember[]>> => {
    const response = await apiClient.get<ApiResponse<StaffMember[]>>('/staff', {
      params: { locationId, includeInactive: includeInactive ? 'true' : undefined },
    });
    return response.data;
  },

  createStaff: async (locationId: string, input: StaffInput): Promise<ApiResponse<StaffMember>> => {
    const response = await apiClient.post<ApiResponse<StaffMember>>('/staff', {
      locationId,
      ...input,
    });
    return response.data;
  },

  updateStaff: async (
    id: string,
    locationId: string,
    patch: StaffPatch,
  ): Promise<ApiResponse<StaffMember>> => {
    const response = await apiClient.patch<ApiResponse<StaffMember>>(`/staff/${id}`, patch, {
      params: { locationId },
    });
    return response.data;
  },
};
